import { api } from '../client';

export interface VendorDevice {
  id: string;
  name: string;
  status: string;
  metrics: {
    cpu_percent?: number;
    memory_percent?: number;
    disk_percent?: number;
    latency_ms?: number;
    tunnel_status?: string;
    uptime_seconds?: number;
    mongodb_status?: string;
    mongodb_errors?: string;
    pm2_status?: string;
    pm2_errors?: string;
    reconnect_count?: number;
    last_error?: string;
    collected_at?: string;
  } | null;
  last_seen?: string;
  uptime?: number;
  created_at?: string;
}

export interface VendorDeviceDetail extends VendorDevice {
  metrics_history: Array<{
    cpu_percent?: number;
    memory_percent?: number;
    disk_percent?: number;
    timestamp: string;
  }>;
}

export interface DevicesListResponse {
  success: boolean;
  devices: VendorDevice[];
  total: number;
  page: number;
  totalPages: number;
}

export interface DevicesParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  health?: string;
  sort?: string;
  order?: string;
}

export interface VendorStats {
  total: number;
  connected: number;
  disconnected: number;
  pending: number;
  error: number;
}

export interface VendorHealth {
  avg_cpu: number;
  avg_memory: number;
  avg_disk: number;
  degraded: number;
  critical: number;
  total_with_metrics: number;
}

export interface VendorActivity {
  device_name: string;
  old_status: string;
  new_status: string;
  timestamp: string;
}

export interface MetricsPoint {
  cpu_percent?: number;
  memory_percent?: number;
  disk_percent?: number;
  timestamp: string;
}

export interface TimelineChange {
  status: string;
  old_status?: string;
  timestamp: string;
}

export interface TimelineAnalytics {
  uptime_percent: number;
  total_downtime_ms: number;
  disconnect_count: number;
  longest_downtime_ms: number;
  avg_downtime_ms: number;
  offline_events: Array<{
    start: string;
    end: string | null;
    duration_ms: number;
  }>;
}

export interface VendorAlert {
  id: string;
  rule_name: string;
  device_name: string;
  metric: string;
  value: number;
  threshold: number;
  severity: string;
  timestamp: string;
}

export const vendorPortalApi = {
  // Alerts history
  getAlerts: (page = 1, limit = 10) =>
    api.get<{ success: boolean; alerts: VendorAlert[]; total: number; page: number; totalPages: number }>(
      '/vendor/api/alerts', { page, limit }
    ),

  // Device list
  listDevices: (params: DevicesParams = {}) =>
    api.get<DevicesListResponse>('/vendor/api/devices', params as Record<string, string | number>),

  // Device detail
  getDevice: (id: string) =>
    api.get<{ success: boolean; device: VendorDeviceDetail }>(`/vendor/api/devices/${id}`),

  // Device metrics history
  getDeviceMetrics: (id: string, hours = 24) =>
    api.get<{ success: boolean; metrics: MetricsPoint[] }>(
      `/vendor/api/devices/${id}/metrics`, { hours }
    ),

  // Device timeline + offline analytics
  getDeviceTimeline: (id: string, hours = 24) =>
    api.get<{
      success: boolean;
      period: { since: string; until: string; hours: number };
      changes: TimelineChange[];
      analytics: TimelineAnalytics;
    }>(`/vendor/api/devices/${id}/timeline`, { hours }),

  // Dashboard stats
  getStats: () =>
    api.get<{ success: boolean; stats: VendorStats }>('/vendor/api/stats'),

  // Health overview
  getHealth: () =>
    api.get<{ success: boolean; health: VendorHealth }>('/vendor/api/health'),

  // Recent activity
  getActivity: () =>
    api.get<{ success: boolean; activity: VendorActivity[] }>('/vendor/api/activity'),
};
